/**
 * Canvas Step Connector
 *
 * Draws dynamic connector lines between step circles using Canvas 2D API.
 * Lines scale with viewport width and respect theme colors.
 */

import { useCallback, useEffect, useRef } from 'react';

interface CanvasStepConnectorProps {
  stepRefs: React.RefObject<(HTMLDivElement | null)[]>;
  currentStepIndex: number;
  completedColor?: string;
  upcomingColor?: string;
  lineWidth?: number;
}

export default function CanvasStepConnector({
  stepRefs,
  currentStepIndex,
  lineWidth = 2,
}: CanvasStepConnectorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const steps = stepRefs.current;
    if (!canvas || !steps) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const parentRect = parent.getBoundingClientRect();

    // Size canvas to parent
    canvas.width = parentRect.width * dpr;
    canvas.height = parentRect.height * dpr;
    canvas.style.width = `${parentRect.width}px`;
    canvas.style.height = `${parentRect.height}px`;
    ctx.scale(dpr, dpr);

    // Resolve theme colors from CSS custom properties
    const rootStyles = getComputedStyle(document.documentElement);
    const successColor =
      rootStyles.getPropertyValue('--color-success').trim() || '#16a34a';
    const borderColor =
      rootStyles.getPropertyValue('--color-border').trim() || '#303855';

    // Draw lines between consecutive step circles
    for (let i = 0; i < steps.length - 1; i++) {
      const from = steps[i];
      const to = steps[i + 1];
      if (!from || !to) continue;

      const fromRect = from.getBoundingClientRect();
      const toRect = to.getBoundingClientRect();

      // Calculate center points relative to parent
      const fromX = fromRect.left + fromRect.width / 2 - parentRect.left;
      const fromY = fromRect.top + fromRect.height / 2 - parentRect.top;
      const toX = toRect.left + toRect.width / 2 - parentRect.left;
      const toY = toRect.top + toRect.height / 2 - parentRect.top;

      // Start line from edge of circle, not center
      const radius = fromRect.width / 2;
      const startX = fromX + radius;
      const endX = toX - radius;

      ctx.beginPath();
      ctx.moveTo(startX, fromY);
      ctx.lineTo(endX, toY);
      ctx.strokeStyle =
        i < currentStepIndex
          ? `oklch(${successColor})`
          : `oklch(${borderColor})`;
      ctx.lineWidth = lineWidth;
      ctx.stroke();
    }
  }, [stepRefs, currentStepIndex, lineWidth]);

  useEffect(() => {
    draw();

    const parent = canvasRef.current?.parentElement;
    if (!parent) return;

    const observer = new ResizeObserver(() => {
      draw();
    });
    observer.observe(parent);

    return () => observer.disconnect();
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0"
      aria-hidden="true"
    />
  );
}
